/**
 * match-review -- Modo top-level del ActionArea para revisar items pendientes de matcheo
 * tras subir factura OCR (workflow Purchase V1.0). Renderiza una tabla interactiva donde
 * el usuario puede ajustar la accion sugerida (MATCH/CREATE/REVIEW) por item y aprobar.
 *
 * Mision: PANEL-COMPRAS-OCR-MATCH-IN-ACTION-AREA-001 (T1+T2+T3).
 * Extension: PHASE-2-EMBUDO-FINANCIERO-V1-001 T11 -- al elegir insumo MATCH mostrar
 * inventario actual + delta proyectado tras ingreso (Inventario actual: X, tras ingreso: X+Y).
 *
 * Payload (showMode('match-review', payload)):
 *   - businessId            (string, requerido)
 *   - facturaId             (string, opcional - se incluye en POST si esta disponible)
 *   - resumen               { items_total, match_sugeridos, crear_sugeridos, review }
 *   - tabla                 (array, requerido) - filas devueltas por el LLM Purchase V1.0
 *     - item_id, factura_descripcion, llave_normalizada, accion, requiere_aprobacion,
 *       insumo_id_elegido, confianza, razon, alertas[], create_sugerido, candidatos[]
 *   - pregunta_al_usuario   (string, opcional)
 *   - matchWebhookUrl       (string, opcional, default n8n.nexodigital360.com/webhook/purchase/match)
 *   - onConfirmed           (function, opcional) callback({ ok, response, aprobaciones })
 *   - onExit                (function, opcional) callback() si el usuario cancela
 *   - ajustes               (opcional, FV-T5) { cargar: async () => items[], guardar: async (cambios) => {ok, error} }
 *                           cantidad y costo unitario editables por item con el subtotal fijo; los cambios se
 *                           guardan ANTES del match (el match crea la ENTRADA con la cantidad de la base)
 *   - rubro                 (opcional, FV-T5 punto 1) { cargar: async () => {opciones, actual, nombre, delProveedor},
 *                           guardar: async (rubroId|null) => {ok, error} }: si el proveedor ya tiene rubro, se muestra
 *
 * Eventos emitidos a ActionArea (ctx.emit):
 *   - 'mode-action': { mode:'match-review', action:'confirmed'|'exited', data }
 *
 * @module presentation/components/ActionAreaModes/match-review
 */

// T11 PHASE-2: catalogo de insumos del negocio para mostrar inventario actual al matchear.
// Import lazy del helper (evita ciclo con el ActionArea si se reorganiza el bundle).
import { InventarioApiClient } from '../../../infrastructure/api/InventarioApiClient.js';
// Con otro nombre: este archivo ya tiene su propia funcion confirm() del matcheo y renderMatchReview recibe un
// payload.confirmar (el camino propio del llamador); un import llamado confirmar quedaria tapado por ese const.
import { confirmar as preguntarEnPagina } from '../../shared/nxDialogo.js';
import { itemsPorId, porCantidad, porCosto, leerNumero, cantidadInventario, cambiosParaGuardar } from '../../../application/compras/ajusteItemFactura.js';

const DEFAULT_MATCH_WEBHOOK = 'https://n8n.nexodigital360.com/webhook/purchase/match';

const ACCION_META = {
  MATCH:  { label: 'Matchear', color: '#3fb950', desc: 'Asociar al insumo elegido' },
  CREATE: { label: 'Crear',    color: '#1f6feb', desc: 'Insumo nuevo en catalogo' },
  REVIEW: { label: 'Revisar',  color: '#d29922', desc: 'Pedir revision a un administrador' }
};

function fmtMoney(n) {
  if (n === null || n === undefined || isNaN(Number(n))) return '$0';
  return '$' + Number(n).toLocaleString('es-CO', { maximumFractionDigits: 0 });
}

export function renderMatchReview(container, payload, ctx) {
  const businessId = payload?.businessId || '';
  const facturaId = payload?.facturaId || '';
  const tabla = Array.isArray(payload?.tabla) ? payload.tabla : [];
  const resumen = payload?.resumen || {};
  const pregunta = payload?.pregunta_al_usuario || '';
  const facturaTotal = payload?.facturaTotal ?? null;
  const facturaCodigo = payload?.facturaCodigo ?? null;
  const matchWebhookUrl = payload?.matchWebhookUrl || DEFAULT_MATCH_WEBHOOK;
  const onConfirmed = typeof payload?.onConfirmed === 'function' ? payload.onConfirmed : null;
  const confirmar = typeof payload?.confirmar === 'function' ? payload.confirmar : null;
  const onExit = typeof payload?.onExit === 'function' ? payload.onExit : null;

  // #2478 item1: labels por modo. Defaults = compras/inventario (backward-compatible:
  // si el caller no pasa payload.labels, el texto es identico al de antes).
  const _L = payload?.labels || {};
  const LBL = {
    header:   _L.header   || '📋 Revisar matcheo de factura',
    intro:    _L.intro    || 'Confirma la accion sugerida por cada item antes de aplicar al inventario.',
    itemNoun: _L.itemNoun || 'insumo',
    confirm:  _L.confirm  || 'Confirmar matcheo',
    apply:    _L.apply    || 'Enviar al inventario',
    // #2531 f3: textos que en modo conciliacion NO deben hablar de compras/inventario/factura.
    fieldLabel:        _L.fieldLabel        || 'Insumo',
    selectPlaceholder: _L.selectPlaceholder || '-- Selecciona insumo --',
    actionLabels:      _L.actionLabels      || null, // { MATCH, CREATE, REVIEW } override opcional
    summaryHint:       _L.summaryHint       || 'Revisa que cada item tenga la accion correcta antes de enviar al inventario.',
    // #2531 f3: en conciliacion el "match" es un pago del sistema, no un insumo -> ocultar bloque inventario.
    showInventory:     _L.showInventory !== false,
    // BAN-T3: el resultado cuando el caller confirma por su propio camino (`confirmar`).
    confirmado:        _L.confirmado     || 'Guardado',
    confirmadoComo:    _L.confirmadoComo || 'guardado', // en singular: el plural le suma la s
    exitConfirm:       _L.exitConfirm || ((n) =>
      `Faltan ${n} item(s) por confirmar match al inventario.\n\n` +
      `Si cierras ahora sin click en "Confirmar matcheo", los items quedaran pendientes en la factura y NO se aplicaran al inventario hasta que los resuelvas manualmente.\n\n` +
      `¿Cerrar de todos modos?`),
  };
  const accionLabel = (a) => (LBL.actionLabels && LBL.actionLabels[a]) || (ACCION_META[a] || ACCION_META.REVIEW).label;

  if (!businessId || tabla.length === 0) {
    container.innerHTML = `
      <div style="padding:18px;color:#d29922;font-size:0.9rem">
        match-review: payload incompleto (businessId + tabla[] requeridos).
      </div>
    `;
    expandContainer(container);
    return;
  }

  // Estado mutable: cada fila puede cambiar accion y/o insumo_id_elegido
  // PHASE-2 T11 (bit #938): preservamos cantidad/unidad/presentacion/precio para el resumen
  // post-confirm (UX cantidad inventario). Si el LLM Purchase no los retorna, fallback a null.
  const rows = tabla.map((t) => ({
    item_id: t.item_id,
    factura_descripcion: t.factura_descripcion || '',
    llave_normalizada: t.llave_normalizada || '',
    accion: (t.accion || 'REVIEW').toUpperCase(),
    insumo_id_elegido: t.insumo_id_elegido || null,
    confianza: typeof t.confianza === 'number' ? t.confianza : null,
    razon: t.razon || '',
    alertas: Array.isArray(t.alertas) ? t.alertas : [],
    candidatos: Array.isArray(t.candidatos) ? t.candidatos : [],
    create_sugerido: t.create_sugerido || null,
    // T11: campos de la factura preservados para post-confirm
    cantidad: t.cantidad ?? null,
    unidad: t.unidad || '',
    presentacion: t.presentacion || '',
    precio_unitario: t.precio_unitario ?? null,
    total_linea: t.total_linea ?? null,
    // TODO T9b: bandera para mostrar 'pendiente conversion' cuando exista en el response
    requiere_conversion: t.requiere_conversion === true
  }));

  // T11 PHASE-2: catalogo de insumos del negocio (cargado async en background) para mostrar
  // inventario actual + delta proyectado tras ingreso. Mientras no llega, las chips se omiten.
  // Cuando llega se hace re-render del frame para popularlas sin perder estado del usuario.
  // Map: insumo_id (string) -> { nombre, cantidadActual:number, unidad:string, grupo:string }
  let insumosCatalog = new Map();
  let catalogLoaded = false;
  // PANEL-FACTURAS-INSUMO-MATCH-FIX-V1-001 T3: indice nombre-normalizado -> {id, nombre}
  // para detectar en el panel si un CREATE duplicaria un insumo existente (anti-duplicado).
  // Normalizacion identica a la del backend (T2): lower(btrim(nombre)).
  let insumosByName = new Map();
  const normNombre = (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');
  // Devuelve el insumo existente que colisiona por nombre, o null.
  const findExistingInsumo = (nombre) => {
    const k = normNombre(nombre);
    return k ? (insumosByName.get(k) || null) : null;
  };

  (async () => {
    try {
      const res = await InventarioApiClient.getInsumosWithConfig(businessId);
      if (res.ok && Array.isArray(res.data)) {
        for (const ins of res.data) {
          if (!ins || !ins.id) continue;
          // Lectura defensiva: el endpoint expone shape canon v1.2 (camelCase) pero el JSDoc
          // del client documenta nombres legacy en mayuscula. Probamos varios casos.
          const cant = Number(
            ins.inventario ?? ins.Inventario ?? ins.stock ?? ins.cantidad ?? 0
          );
          const unidad =
            ins.unidad_conteo ?? ins.unidadConteo ?? ins.Unidad ?? ins.unidad ?? '';
          const nombre = ins.nombre || ins.Nombre || '';
          insumosCatalog.set(String(ins.id), {
            nombre,
            cantidadActual: isFinite(cant) ? cant : 0,
            unidad: String(unidad || ''),
            grupo: ins.grupo || ins.Grupo || ''
          });
          // T3: primer insumo gana ante colision de nombre (el mas antiguo suele ser el canonico).
          const nk = normNombre(nombre);
          if (nk && !insumosByName.has(nk)) insumosByName.set(nk, { id: String(ins.id), nombre });
        }
        catalogLoaded = true;
        renderFrame();
      }
    } catch { /* silent - chips quedan sin datos, no rompe el flujo */ }
  })();

  // FV-T5: cantidad y costo unitario de cada item, en unidades de la factura, con el subtotal fijo. null = cargando;
  // un Map vacio = no se pudieron leer (la revision sigue como antes, sin corregir numeros).
  const ajustes = payload?.ajustes && typeof payload.ajustes.cargar === 'function' && typeof payload.ajustes.guardar === 'function'
    ? payload.ajustes : null;
  let itemsFactura = null;
  const editados = new Map();
  const invalidos = new Set();
  // FV-T5 punto 1: el rubro del proveedor, arriba. { cargar: async () => {opciones, actual, nombre, delProveedor},
  // guardar: async (rubroId|null) => {ok, error} }. Si el proveedor ya lo tiene, se muestra y no se pregunta.
  const rubro = payload?.rubro && typeof payload.rubro.cargar === 'function' && typeof payload.rubro.guardar === 'function'
    ? payload.rubro : null;
  let rubroInfo = null;
  let cambiandoRubro = false;
  let rubroEstado = '';
  if (rubro) {
    (async () => {
      try { rubroInfo = await rubro.cargar(); } catch { rubroInfo = null; }
      if (rubroInfo && container.isConnected && container.querySelector('[data-aa-match-body]')) renderFrame();
    })();
  }
  if (ajustes) {
    (async () => {
      try { itemsFactura = itemsPorId(await ajustes.cargar()); } catch { itemsFactura = new Map(); }
      if (container.isConnected && container.querySelector('[data-aa-match-body]')) renderFrame();
    })();
  }

  expandContainer(container);
  renderFrame();

  function renderFrame() {
    const matchCount = rows.filter(r => r.accion === 'MATCH' && r.insumo_id_elegido).length;
    const createCount = rows.filter(r => r.accion === 'CREATE').length;
    const reviewCount = rows.filter(r => r.accion === 'REVIEW').length;
    const total = rows.length;
    // T3 PANEL-POST-OCR-MATCH-REDIRECT-V1-001 (bit #971): pendientes = items sin
    // resolver "limpiamente" (MATCH sin insumo elegido). CREATE y REVIEW son decisiones
    // validas que se materializan al confirmar -> NO cuentan como pendientes visualmente.
    const pendientesPanel = rows.filter(r => r.accion === 'MATCH' && !r.insumo_id_elegido).length;

    container.innerHTML = `
      <div class="aa-match-review" data-aa-mode="match-review">
        <header style="display:flex;align-items:flex-start;justify-content:space-between;gap:10px;padding:14px 16px 8px;border-bottom:1px solid #21262d">
          <div style="min-width:0;flex:1">
            <h3 style="margin:0;font-size:0.98rem;color:#f0f6fc;line-height:1.3">${ctx.escHtml(LBL.header)}</h3>
            <div style="margin-top:4px;font-size:0.875rem;color:#8b949e">
              ${ctx.escHtml(pregunta || LBL.intro)}
            </div>
            <div style="margin-top:8px;display:flex;gap:8px;flex-wrap:wrap;font-size:0.875rem">
              <span style="padding:2px 8px;background:#3fb95022;color:#3fb950;border-radius:10px">MATCH ${matchCount}</span>
              <span style="padding:2px 8px;background:#1f6feb22;color:#58a6ff;border-radius:10px">CREATE ${createCount}</span>
              <span style="padding:2px 8px;background:#d2992222;color:#d29922;border-radius:10px">REVIEW ${reviewCount}</span>
              <span style="padding:2px 8px;background:#161b22;color:#8b949e;border-radius:10px">Total ${total}</span>
              ${pendientesPanel > 0 ? `<span style="padding:2px 8px;background:#f8514922;color:#f85149;border-radius:10px;font-weight:600">⚠ Falta elegir ${ctx.escHtml(LBL.itemNoun)} en ${pendientesPanel}</span>` : ''}
            </div>
            ${renderRubroLinea()}
          </div>
          <button data-aa-match-close type="button" title="Cerrar"
                  style="background:transparent;border:none;color:#8b949e;cursor:pointer;font-size:1.1rem;padding:0 4px">×</button>
        </header>

        <div data-aa-match-body class="aa-match-body" style="padding:8px 16px;max-height:340px;overflow-y:auto">
          ${rows.map((r, i) => renderRow(r, i)).join('')}
        </div>

        <footer style="display:flex;align-items:center;justify-content:space-between;gap:8px;padding:10px 16px 14px;border-top:1px solid #21262d">
          <div style="font-size:0.875rem;color:#6e7681">
            <span data-aa-match-status></span>
          </div>
          <div style="display:flex;gap:8px">
            <button data-aa-match-cancel type="button"
                    style="padding:6px 14px;background:transparent;border:1px solid #30363d;border-radius:5px;color:#c9d1d9;cursor:pointer;font-size:0.875rem">
              Cancelar
            </button>
            <button data-aa-match-confirm type="button"
                    style="padding:6px 16px;background:${matchCount > 0 ? '#238636' : '#30363d'};border:1px solid ${matchCount > 0 ? '#238636' : '#30363d'};border-radius:5px;color:#fff;cursor:${matchCount > 0 ? 'pointer' : 'not-allowed'};font-size:0.875rem"
                    ${matchCount > 0 ? '' : 'disabled'}>
              ✓ ${ctx.escHtml(LBL.confirm)} (${matchCount})
            </button>
          </div>
        </footer>
      </div>
    `;

    container.querySelector('[data-aa-match-close]').addEventListener('click', exit);
    container.querySelector('[data-aa-match-cancel]').addEventListener('click', exit);
    // B7-P fix: en lugar de invocar confirm() directo, mostrar pantalla resumen pre-confirmacion
    // para que el usuario verifique TODOS los items antes de enviar al webhook (evita perder
    // items por scroll - bug smoke COCA COLA Fenix).
    container.querySelector('[data-aa-match-confirm]').addEventListener('click', renderResumenPreConfirmar);

    // Wire dropdowns por fila
    rows.forEach((r, i) => {
      const $accion = container.querySelector(`[data-aa-match-accion="${i}"]`);
      const $insumo = container.querySelector(`[data-aa-match-insumo="${i}"]`);
      if ($accion) {
        $accion.addEventListener('change', (e) => {
          rows[i].accion = e.target.value;
          // Si pasa a CREATE/REVIEW, limpiar insumo elegido
          if (e.target.value !== 'MATCH') rows[i].insumo_id_elegido = null;
          renderFrame();
        });
      }
      if ($insumo) {
        $insumo.addEventListener('change', (e) => {
          rows[i].insumo_id_elegido = e.target.value || null;
          renderFrame();
        });
      }
    });

    // FV-T5: corregir cantidad o costo unitario (al salir del campo). El subtotal no se mueve: el otro numero se
    // recalcula. Un valor que no es un numero > 0 queda marcado y no se guarda.
    const corregir = (i, campo, texto) => {
      const r = rows[i];
      const base = itemsFactura?.get(String(r.item_id));
      if (!base) return;
      const actual = editados.get(base.id) || base;
      const n = leerNumero(texto);
      const nuevo = n === null ? null : campo === 'cantidad' ? porCantidad(actual, n) : porCosto(actual, n);
      if (!nuevo) { invalidos.add(`${base.id}:${campo}`); renderFrame(); return; }
      invalidos.delete(`${base.id}:cantidad`); invalidos.delete(`${base.id}:costo`);
      if (Math.abs(nuevo.cantidad - base.cantidad) < 1e-6) editados.delete(base.id); else editados.set(base.id, nuevo);
      r.cantidad = cantidadInventario(nuevo); // el bloque de inventario muestra lo que va a entrar
      renderFrame();
    };
    container.querySelector('[data-aa-rubro-cambiar]')?.addEventListener('click', () => { cambiandoRubro = true; renderFrame(); });
    const $rubroSel = container.querySelector('[data-aa-rubro-select]');
    $rubroSel?.addEventListener('change', async () => {
      const id = $rubroSel.value || null;
      $rubroSel.disabled = true;
      rubroEstado = 'guardando…';
      let g = null;
      try { g = await rubro.guardar(id === null ? null : Number(id)); } catch (err) { g = { ok: false, error: err?.message || String(err) }; }
      if (g?.ok) {
        const op = (rubroInfo.opciones || []).find((o) => String(o.id) === String(id));
        rubroInfo = { ...rubroInfo, actual: id, nombre: op?.nombre ?? null, delProveedor: id !== null };
        cambiandoRubro = false;
        rubroEstado = id === null ? 'sin rubro' : 'guardado en el proveedor';
      } else {
        rubroEstado = `no se guardó (${g?.error || 'error'})`;
      }
      renderFrame();
    });

    container.querySelectorAll('[data-aa-aj-cantidad]').forEach(($in) => $in.addEventListener('change', () => corregir(Number($in.dataset.aaAjCantidad), 'cantidad', $in.value)));
    container.querySelectorAll('[data-aa-aj-costo]').forEach(($in) => $in.addEventListener('change', () => corregir(Number($in.dataset.aaAjCosto), 'costo', $in.value)));

    // PANEL-FACTURAS-INSUMO-MATCH-FIX-V1-001 T3: "Usar el existente" -> cambia la fila a
    // MATCH con el insumo ya existente (lo agrega como candidato si no estaba) para no duplicar.
    container.querySelectorAll('[data-aa-use-existing]').forEach(btn => {
      btn.addEventListener('click', () => {
        const i = Number(btn.dataset.aaUseExisting);
        const r = rows[i];
        if (!r) return;
        const nombre = (r.create_sugerido && r.create_sugerido.nombre) || r.factura_descripcion || '';
        const existing = findExistingInsumo(nombre);
        if (!existing) return;
        r.accion = 'MATCH';
        r.insumo_id_elegido = existing.id;
        if (!Array.isArray(r.candidatos)) r.candidatos = [];
        if (!r.candidatos.some(c => String(c.insumo_id) === existing.id)) {
          r.candidatos = [{ insumo_id: existing.id, nombre: existing.nombre, presentacion: '', unidad: '', score: 1 }, ...r.candidatos];
        }
        renderFrame();
      });
    });
  }

  /**
   * T11 PHASE-2: formatea numero en es-CO con separador de miles, max 3 decimales,
   * sin trailing zeros. "12500" -> "12.500", "1.5" -> "1,5", "0.005" -> "0,005".
   */
  function fmtCantidad(n) {
    if (n === null || n === undefined || isNaN(Number(n))) return '0';
    const num = Number(n);
    return num.toLocaleString('es-CO', { maximumFractionDigits: 3 });
  }

  /**
   * T11 PHASE-2: bloque "Inventario actual + delta" para una row MATCH con insumo elegido.
   * Devuelve HTML string para insertar grid-column:1/-1 dentro del row.
   *
   * Casos:
   *  - catalog no cargado aun -> chip placeholder con "cargando..."
   *  - catalog cargado + insumo no encontrado (sync legacy sin id BD) -> chip warning
   *  - cantidad factura presente + misma unidad -> muestra delta tras ingreso
   *  - cantidad factura ausente o unidades distintas -> solo muestra actual + nota
   */
  function renderInventarioActualBlock(r) {
    if (!LBL.showInventory) return ''; // #2531 f3: conciliacion no toca inventario.
    if (r.accion !== 'MATCH' || !r.insumo_id_elegido) return '';
    if (!catalogLoaded) {
      return `
        <div style="grid-column:1/-1;margin-top:6px;padding:5px 10px;background:#161b22;border-left:3px solid #30363d;border-radius:3px;font-size:0.875rem;color:#8b949e">
          Cargando inventario actual...
        </div>`;
    }
    const inv = insumosCatalog.get(String(r.insumo_id_elegido));
    if (!inv) {
      return `
        <div style="grid-column:1/-1;margin-top:6px;padding:5px 10px;background:#161b22;border-left:3px solid #d29922;border-radius:3px;font-size:0.875rem;color:#d29922">
          Sin datos de inventario actual para este insumo (puede ser un insumo legacy fuera del catalogo canon).
        </div>`;
    }
    const unidad = inv.unidad || '';
    const cantAct = inv.cantidadActual;
    const cantFactura = Number(r.cantidad);
    const unidadFactura = String(r.unidad || '').trim();
    let deltaBlock = '';
    if (isFinite(cantFactura) && cantFactura > 0) {
      const mismaUnidad = !unidadFactura || !unidad || unidadFactura.toLowerCase() === unidad.toLowerCase();
      if (mismaUnidad) {
        const total = cantAct + cantFactura;
        deltaBlock = ` <span style="color:#8b949e">+</span> <strong style="color:#58a6ff">${ctx.escHtml(fmtCantidad(cantFactura))}${unidad ? ' ' + ctx.escHtml(unidad) : ''}</strong> <span style="color:#8b949e">=</span> <strong style="color:#3fb950">${ctx.escHtml(fmtCantidad(total))}${unidad ? ' ' + ctx.escHtml(unidad) : ''}</strong>`;
      } else {
        deltaBlock = ` <span style="color:#d29922;font-size:0.875rem">(factura: ${ctx.escHtml(fmtCantidad(cantFactura))} ${ctx.escHtml(unidadFactura)} -- Desktop convierte segun presentacion)</span>`;
      }
    }
    return `
      <div style="grid-column:1/-1;margin-top:6px;padding:6px 10px;background:#0d2818;border-left:3px solid #3fb950;border-radius:3px;font-size:0.875rem;color:#c9d1d9;line-height:1.5">
        <span style="color:#8b949e">Inventario actual:</span> <strong style="color:#f0f6fc">${ctx.escHtml(fmtCantidad(cantAct))}${unidad ? ' ' + ctx.escHtml(unidad) : ''}</strong>${deltaBlock}
      </div>`;
  }

  // PANEL-FACTURAS-INSUMO-MATCH-FIX-V1-001 T3: si un CREATE tiene el mismo nombre normalizado
  // que un insumo ya existente, avisar y ofrecer usar el existente (evita duplicado accidental).
  function renderDuplicadoWarning(r, idx) {
    if (!catalogLoaded) return ''; // sin catalogo no podemos afirmar que exista
    const nombre = (r.create_sugerido && r.create_sugerido.nombre) || r.factura_descripcion || '';
    const existing = findExistingInsumo(nombre);
    if (!existing) return '';
    return `
      <div style="grid-column:1/-1;margin-top:6px;padding:8px 10px;background:#d2992218;border:1px solid #d29922;border-radius:6px;font-size:0.875rem;color:#e3b341;line-height:1.5">
        ⚠ Ya existe un insumo <strong>"${ctx.escHtml(existing.nombre)}"</strong> con este nombre. Crear uno nuevo lo <strong>duplicaría</strong>.
        <button type="button" data-aa-use-existing="${idx}" style="margin-left:8px;padding:2px 10px;background:#238636;border:1px solid #238636;border-radius:4px;color:#fff;cursor:pointer;font-size:0.875rem;font-weight:600">Usar el existente</button>
      </div>`;
  }

  /**
   * FV-T5: cantidad y costo unitario del item, editables, con el subtotal fijo. Sin `ajustes` (conciliacion de
   * banco y quien no lo pase) no pinta nada.
   */
  /** FV-T5 punto 1: el rubro del proveedor. Ya asignado en la base -> se muestra; si no, se elige aqui. */
  function renderRubroLinea() {
    if (!rubro || !rubroInfo || !(rubroInfo.opciones || []).length) return '';
    const estado = rubroEstado ? ` <span data-aa-rubro-estado style="color:#8b949e">${ctx.escHtml(rubroEstado)}</span>` : '';
    if (rubroInfo.delProveedor && rubroInfo.actual && !cambiandoRubro) {
      return `<div data-aa-rubro style="margin-top:8px;font-size:0.875rem;color:#c9d1d9">🏷️ Rubro del proveedor: <strong data-aa-rubro-nombre>${ctx.escHtml(rubroInfo.nombre || rubroInfo.actual)}</strong>`
        + ` <button type="button" data-aa-rubro-cambiar style="margin-left:6px;background:none;border:0;color:#58a6ff;cursor:pointer;font-size:0.875rem;padding:0">cambiar</button>${estado}</div>`;
    }
    const opts = `<option value="">— elige el rubro —</option>` + rubroInfo.opciones.map((o) =>
      `<option value="${ctx.escHtml(o.id)}"${String(o.id) === String(rubroInfo.actual) ? ' selected' : ''}>${ctx.escHtml(o.nombre)}</option>`).join('');
    return `<div data-aa-rubro style="margin-top:8px;display:flex;align-items:center;gap:6px;flex-wrap:wrap;font-size:0.875rem;color:#c9d1d9">🏷️ Rubro del proveedor:`
      + ` <select data-aa-rubro-select style="padding:3px 6px;background:#161b22;border:1px solid #30363d;border-radius:4px;color:#c9d1d9;font-size:0.875rem">${opts}</select>`
      + ` <span style="color:#6e7681">se guarda en el proveedor: sus próximas facturas lo heredan</span>${estado}</div>`;
  }

  function renderAjusteBlock(r, idx) {
    if (!ajustes) return '';
    const caja = (inner, borde = '#30363d') => `<div data-aa-aj-item="${ctx.escHtml(r.item_id)}" style="margin:0 0 8px;padding:6px 10px;background:#161b22;border-left:3px solid ${borde};border-radius:3px;font-size:0.875rem;color:#c9d1d9;line-height:1.6">${inner}</div>`;
    if (itemsFactura === null) return caja('<span style="color:#8b949e">Cargando cantidad y costo de la factura…</span>');
    const base = itemsFactura.get(String(r.item_id));
    if (!base) return '';
    if (!base.ajustable) return caja('<span style="color:#d29922">Este item no trae subtotal o cantidad: corrígelo después en el detalle de la factura.</span>', '#d29922');
    const it = editados.get(base.id) || base;
    const corregido = editados.has(base.id);
    const und = base.unidad ? ' ' + ctx.escHtml(base.unidad) : '';
    const malo = (c) => invalidos.has(`${base.id}:${c}`);
    const input = (attr, valor, c) => `<input ${attr}="${idx}" inputmode="decimal" value="${ctx.escHtml(fmtCantidad(valor))}" style="width:96px;padding:3px 6px;background:#0d1117;border:1px solid ${malo(c) ? '#f85149' : '#30363d'};border-radius:4px;color:#f0f6fc;font-size:0.875rem;text-align:right">`;
    const inv = base.factor && base.factor !== 1 ? ` <span style="color:#8b949e">= ${ctx.escHtml(fmtCantidad(cantidadInventario(it)))} al inventario</span>` : '';
    return caja(
      `<span style="color:#8b949e">Cantidad</span> ${input('data-aa-aj-cantidad', it.cantidad, 'cantidad')}${und}${inv}`
      + ` &nbsp;<span style="color:#8b949e">Costo unitario $</span>${input('data-aa-aj-costo', Math.round(it.costo * 100) / 100, 'costo')}`
      + ` &nbsp;<span style="color:#8b949e">Subtotal</span> <strong data-aa-aj-subtotal style="color:#f0f6fc">${ctx.escHtml(fmtMoney(base.subtotal))}</strong> <span style="color:#6e7681">(lo que cobró el proveedor, fijo)</span>`
      + (corregido ? `<div data-aa-aj-corregido style="color:#e3b341">Corregido: la factura decía ${ctx.escHtml(fmtCantidad(base.cantidad))}${und} a ${ctx.escHtml(fmtMoney(base.costo))}.</div>` : '')
      + (malo('cantidad') || malo('costo') ? '<div style="color:#f85149">Escribe un número mayor que cero.</div>' : ''),
      corregido ? '#e3b341' : '#30363d');
  }

  function renderRow(r, idx) {
    const meta = ACCION_META[r.accion] || ACCION_META.REVIEW;
    const candidatosOpts = [
      `<option value="">${ctx.escHtml(LBL.selectPlaceholder)}</option>`,
      ...r.candidatos.map(c => {
        const sel = r.insumo_id_elegido === c.insumo_id ? ' selected' : '';
        const score = typeof c.score === 'number' ? ` (${Math.round(c.score * 100)}%)` : '';
        return `<option value="${ctx.escHtml(c.insumo_id)}"${sel}>${ctx.escHtml(c.nombre || '')} ${ctx.escHtml(c.presentacion || '')} ${ctx.escHtml(c.unidad || '')}${score}</option>`;
      })
    ].join('');

    const accionOpts = ['MATCH', 'CREATE', 'REVIEW'].map(a => {
      const sel = r.accion === a ? ' selected' : '';
      const disabled = (a === 'MATCH' && r.candidatos.length === 0) ? ' disabled' : '';
      return `<option value="${a}"${sel}${disabled}>${ctx.escHtml(accionLabel(a))}</option>`;
    }).join('');

    // B7-P fix: numeracion explicita "Item N/Total" en cada row para que el usuario sepa
    // cuantos items quedan por revisar (evita perder items por no notar scroll).
    const rowNumber = `<span style="display:inline-block;min-width:40px;padding:1px 6px;background:#21262d;color:#8b949e;border-radius:3px;font-size:0.875rem;font-weight:600;font-family:monospace;margin-right:6px">${idx + 1}/${rows.length}</span>`;
    return `
      <div data-aa-match-row="${idx}" style="padding:10px 12px;border:1px solid #21262d;border-radius:6px;margin-bottom:8px;background:#0d1117">
        <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:10px;margin-bottom:8px">
          <div style="flex:1;min-width:0">
            <div style="font-size:0.875rem;color:#f0f6fc;font-weight:500">${rowNumber}${ctx.escHtml(r.factura_descripcion)}</div>
            ${r.llave_normalizada ? `<div style="font-size:0.875rem;color:#6e7681;margin-top:2px;font-family:monospace">${ctx.escHtml(r.llave_normalizada)}</div>` : ''}
          </div>
          <span style="padding:2px 8px;background:${meta.color}22;color:${meta.color};border-radius:10px;font-size:0.875rem;white-space:nowrap">
            ${ctx.escHtml(accionLabel(r.accion))}${typeof r.confianza === 'number' ? ' ' + Math.round(r.confianza * 100) + '%' : ''}
          </span>
        </div>

        ${renderAjusteBlock(r, idx)}

        ${r.razon ? `<div style="font-size:0.875rem;color:#8b949e;margin-bottom:8px;font-style:italic">${ctx.escHtml(r.razon)}</div>` : ''}

        <div style="display:grid;grid-template-columns:120px 1fr;gap:8px;align-items:center">
          <label style="font-size:0.875rem;color:#8b949e">Accion</label>
          <select data-aa-match-accion="${idx}" style="padding:5px 8px;background:#161b22;border:1px solid #30363d;border-radius:4px;color:#c9d1d9;font-size:0.875rem">
            ${accionOpts}
          </select>

          ${r.accion === 'MATCH' ? `
            <label style="font-size:0.875rem;color:#8b949e">${ctx.escHtml(LBL.fieldLabel)}</label>
            <select data-aa-match-insumo="${idx}" style="padding:5px 8px;background:#161b22;border:1px solid #30363d;border-radius:4px;color:#c9d1d9;font-size:0.875rem">
              ${candidatosOpts}
            </select>
            ${renderInventarioActualBlock(r)}
          ` : ''}

          ${r.accion === 'CREATE' && r.create_sugerido ? `
            <label style="font-size:0.875rem;color:#8b949e">Crear como</label>
            <div style="font-size:0.875rem;color:#79c0ff">
              ${ctx.escHtml(r.create_sugerido.nombre || '')} · ${ctx.escHtml(r.create_sugerido.presentacion || '')} · ${ctx.escHtml(r.create_sugerido.unidad || '')}
            </div>
            ${renderDuplicadoWarning(r, idx)}
          ` : ''}

          ${r.accion === 'REVIEW' ? `
            <label style="font-size:0.875rem;color:#8b949e">En revision</label>
            <div style="font-size:0.875rem;color:#d29922">
              Al enviar a revision, un administrador o supervisor revisara este item para tomar la decision final.
            </div>
          ` : ''}
        </div>

        ${r.alertas.length > 0 ? `
          <div style="margin-top:8px;display:flex;flex-wrap:wrap;gap:4px">
            ${r.alertas.map(a => `<span style="padding:1px 6px;background:#d2992222;color:#d29922;border-radius:3px;font-size:0.875rem">⚠ ${ctx.escHtml(a)}</span>`).join('')}
          </div>
        ` : ''}
      </div>
    `;
  }

  /**
   * B7-P fix: pantalla resumen pre-confirmacion. Muestra TODOS los items con su accion
   * final + insumo elegido (si MATCH) en una vista compacta sin scroll para que el usuario
   * verifique antes de enviar al webhook. Evita el bug smoke COCA COLA donde Fenix perdio
   * 2/4 items al confirmar sin ver todo el scroll del modal.
   */
  /** FV-T5: en el resumen, lo corregido se ve antes de enviar (de -> a). */
  function lineaCorregida(r) {
    const base = itemsFactura?.get(String(r.item_id));
    const e = base && editados.get(base.id);
    if (!e) return '';
    const und = base.unidad ? ' ' + ctx.escHtml(base.unidad) : '';
    return `<div data-aa-resumen-corregido style="flex-basis:100%;font-size:0.875rem;color:#e3b341;padding-left:48px;margin-top:2px">`
      + `Corregido: ${ctx.escHtml(fmtCantidad(base.cantidad))}${und} a ${ctx.escHtml(fmtMoney(base.costo))} → ${ctx.escHtml(fmtCantidad(e.cantidad))}${und} a ${ctx.escHtml(fmtMoney(e.costo))} (subtotal ${ctx.escHtml(fmtMoney(base.subtotal))})</div>`;
  }

  function renderResumenPreConfirmar() {
    const total = rows.length;
    const matchOk = rows.filter(r => r.accion === 'MATCH' && r.insumo_id_elegido).length;
    const matchSinInsumo = rows.filter(r => r.accion === 'MATCH' && !r.insumo_id_elegido).length;
    const createCount = rows.filter(r => r.accion === 'CREATE').length;
    const reviewCount = rows.filter(r => r.accion === 'REVIEW').length;

    const itemsListHtml = rows.map((r, i) => {
      const meta = ACCION_META[r.accion] || ACCION_META.REVIEW;
      let detalle = '';
      // T11 PHASE-2: linea secundaria "Inventario: X → tras ingreso: X+Y = Z" cuando MATCH+insumo elegido.
      let invLine = '';
      if (r.accion === 'MATCH') {
        if (r.insumo_id_elegido) {
          const cand = r.candidatos.find(c => c.insumo_id === r.insumo_id_elegido);
          detalle = `<span style="color:#3fb950">→ ${ctx.escHtml(cand?.nombre || 'matcheado')}</span>`;
          if (LBL.showInventory && catalogLoaded) {
            const inv = insumosCatalog.get(String(r.insumo_id_elegido));
            if (inv) {
              const unidad = inv.unidad || '';
              const cantAct = inv.cantidadActual;
              const cantFactura = Number(r.cantidad);
              const unidadFactura = String(r.unidad || '').trim();
              const mismaUnidad = !unidadFactura || !unidad || unidadFactura.toLowerCase() === unidad.toLowerCase();
              if (isFinite(cantFactura) && cantFactura > 0 && mismaUnidad) {
                const total = cantAct + cantFactura;
                invLine = `
                  <div style="flex-basis:100%;font-size:0.875rem;color:#8b949e;padding-left:48px;margin-top:2px;line-height:1.4">
                    Inventario: <strong style="color:#f0f6fc">${ctx.escHtml(fmtCantidad(cantAct))}${unidad ? ' ' + ctx.escHtml(unidad) : ''}</strong>
                    + <strong style="color:#58a6ff">${ctx.escHtml(fmtCantidad(cantFactura))}</strong>
                    = <strong style="color:#3fb950">${ctx.escHtml(fmtCantidad(total))}${unidad ? ' ' + ctx.escHtml(unidad) : ''}</strong>
                  </div>`;
              } else {
                invLine = `
                  <div style="flex-basis:100%;font-size:0.875rem;color:#8b949e;padding-left:48px;margin-top:2px">
                    Inventario actual: <strong style="color:#f0f6fc">${ctx.escHtml(fmtCantidad(cantAct))}${unidad ? ' ' + ctx.escHtml(unidad) : ''}</strong>
                  </div>`;
              }
            }
          }
        } else {
          detalle = `<span style="color:#f85149;font-weight:600">⚠ falta elegir ${ctx.escHtml(LBL.fieldLabel.toLowerCase())}</span>`;
        }
      } else if (r.accion === 'CREATE') {
        detalle = `<span style="color:#79c0ff">→ crear ${ctx.escHtml(r.create_sugerido?.nombre || 'nuevo')}</span>`;
      } else if (r.accion === 'REVIEW') {
        detalle = `<span style="color:#d29922">→ enviar a revisor</span>`;
      }
      return `
        <li style="padding:6px 10px;border:1px solid #21262d;border-radius:4px;margin-bottom:4px;background:#0d1117;display:flex;align-items:center;gap:8px;flex-wrap:wrap">
          <span style="display:inline-block;min-width:36px;padding:1px 6px;background:#21262d;color:#8b949e;border-radius:3px;font-size:0.875rem;font-weight:600;font-family:monospace">${i + 1}/${total}</span>
          <span style="flex:1;min-width:140px;font-size:0.875rem;color:#f0f6fc">${ctx.escHtml(r.factura_descripcion)}</span>
          <span style="padding:1px 8px;background:${meta.color}22;color:${meta.color};border-radius:8px;font-size:0.875rem;white-space:nowrap">${ctx.escHtml(meta.label)}</span>
          <span style="font-size:0.875rem">${detalle}</span>
          ${invLine}
          ${lineaCorregida(r)}
        </li>
      `;
    }).join('');

    const puedeEnviar = matchOk > 0;
    const warningSinInsumo = matchSinInsumo > 0
      ? `<div style="margin-top:6px;padding:6px 10px;background:#f8514922;border:1px dashed #f85149;border-radius:4px;color:#f85149;font-size:0.875rem">
           ⚠ ${matchSinInsumo} item${matchSinInsumo === 1 ? '' : 's'} con accion MATCH sin ${ctx.escHtml(LBL.fieldLabel.toLowerCase())} elegido. Vuelve atras para corregir o cambia su accion a REVIEW/CREATE.
         </div>`
      : '';

    container.innerHTML = `
      <div class="aa-match-resumen" style="padding:14px 16px">
        <header style="display:flex;align-items:flex-start;justify-content:space-between;gap:10px;margin-bottom:10px">
          <div style="flex:1">
            <h3 style="margin:0;font-size:0.98rem;color:#f0f6fc">📋 Resumen pre-confirmacion</h3>
            <div style="margin-top:4px;font-size:0.875rem;color:#8b949e">
              ${ctx.escHtml(LBL.summaryHint)}
            </div>
            <div style="margin-top:8px;display:flex;gap:6px;flex-wrap:wrap;font-size:0.875rem">
              <span style="padding:2px 8px;background:#3fb95022;color:#3fb950;border-radius:10px">✓ MATCH ${matchOk}</span>
              ${matchSinInsumo > 0 ? `<span style="padding:2px 8px;background:#f8514922;color:#f85149;border-radius:10px;font-weight:600">⚠ MATCH sin ${ctx.escHtml(LBL.fieldLabel.toLowerCase())} ${matchSinInsumo}</span>` : ''}
              <span style="padding:2px 8px;background:#1f6feb22;color:#58a6ff;border-radius:10px">CREATE ${createCount}</span>
              <span style="padding:2px 8px;background:#d2992222;color:#d29922;border-radius:10px">REVIEW ${reviewCount}</span>
              <span style="padding:2px 8px;background:#161b22;color:#8b949e;border-radius:10px">Total ${total}</span>
            </div>
          </div>
          <button data-aa-resumen-close type="button" title="Cerrar"
                  style="background:transparent;border:none;color:#8b949e;cursor:pointer;font-size:1.1rem;padding:0 4px">×</button>
        </header>

        ${warningSinInsumo}

        <ul data-aa-resumen-list style="list-style:none;margin:10px 0;padding:0;max-height:280px;overflow-y:auto">
          ${itemsListHtml}
        </ul>

        <footer style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-top:10px;padding-top:10px;border-top:1px solid #21262d">
          <button data-aa-resumen-back type="button"
                  style="padding:6px 14px;background:transparent;border:1px solid #30363d;border-radius:5px;color:#c9d1d9;cursor:pointer;font-size:0.875rem">
            ← Volver atras
          </button>
          <button data-aa-resumen-go type="button" ${puedeEnviar ? '' : 'disabled'}
                  style="padding:6px 18px;background:${puedeEnviar ? '#238636' : '#30363d'};border:1px solid ${puedeEnviar ? '#238636' : '#30363d'};border-radius:5px;color:#fff;cursor:${puedeEnviar ? 'pointer' : 'not-allowed'};font-size:0.875rem;font-weight:500">
            ✓ ${ctx.escHtml(LBL.apply)} (${matchOk})
          </button>
        </footer>
      </div>
    `;

    container.querySelector('[data-aa-resumen-close]').addEventListener('click', exit);
    container.querySelector('[data-aa-resumen-back]').addEventListener('click', renderFrame);
    if (puedeEnviar) {
      container.querySelector('[data-aa-resumen-go]').addEventListener('click', confirm);
    }
  }

  async function confirm() {
    const aprobaciones = rows
      .filter(r => r.accion === 'MATCH' && r.insumo_id_elegido)
      .map(r => ({
        item_id: r.item_id,
        accion: 'MATCH',
        insumo_id: r.insumo_id_elegido,
        confianza: r.confianza ?? 0.9
      }));

    if (aprobaciones.length === 0) {
      const $status = container.querySelector('[data-aa-match-status]');
      if ($status) {
        $status.style.color = '#d29922';
        $status.textContent = `Selecciona al menos un MATCH con ${LBL.fieldLabel.toLowerCase()} antes de confirmar.`;
      }
      return;
    }

    // FV-T5: las correcciones de cantidad/costo se guardan ANTES del match: el n8n crea la ENTRADA leyendo la cantidad
    // de la base. Si no se guardan, NO se manda el match (entraria el numero equivocado del OCR).
    if (ajustes && itemsFactura) {
      const cambios = cambiosParaGuardar(itemsFactura, editados);
      if (cambios.length) {
        const $go = container.querySelector('[data-aa-resumen-go]');
        const avisar = (texto, color) => {
          let $m = container.querySelector('[data-aa-aj-aviso]');
          if (!$m) {
            $m = document.createElement('div');
            $m.setAttribute('data-aa-aj-aviso', '');
            $m.style.cssText = 'margin-top:6px;padding:6px 10px;border-radius:4px;font-size:0.875rem';
            (container.querySelector('[data-aa-resumen-list]') || container.firstElementChild || container).before($m);
          }
          $m.style.color = color;
          $m.style.border = `1px dashed ${color}`;
          $m.textContent = texto;
        };
        if ($go) { $go.disabled = true; $go.textContent = 'Guardando correcciones...'; }
        avisar(`Guardando ${cambios.length} correccion(es) de cantidad y costo...`, '#79c0ff');
        let g = null;
        try { g = await ajustes.guardar(cambios); } catch (err) { g = { ok: false, error: err?.message || String(err) }; }
        if (!g?.ok) {
          avisar(`No se guardaron las correcciones (${g?.error || 'error'}). No se envio nada al inventario: intenta de nuevo.`, '#f85149');
          if ($go) { $go.disabled = false; $go.textContent = `✓ ${LBL.apply} (${aprobaciones.length})`; }
          return;
        }
        // Ya en la base: la factura queda con lo corregido como su nuevo original.
        for (const c of cambios) { const e = editados.get(c.id); if (e) itemsFactura.set(c.id, e); editados.delete(c.id); }
        avisar(`Correcciones guardadas (${cambios.length}). Enviando al inventario...`, '#3fb950');
      }
    }

    const $confirmBtn = container.querySelector('[data-aa-match-confirm]');
    const $status = container.querySelector('[data-aa-match-status]');
    if ($confirmBtn) {
      $confirmBtn.disabled = true;
      $confirmBtn.textContent = 'Enviando...';
      $confirmBtn.style.cursor = 'not-allowed';
    }
    if ($status) {
      $status.style.color = '#79c0ff';
      $status.textContent = `Enviando ${aprobaciones.length} aprobacion(es)...`;
    }

    let response = null;
    let ok = false;
    // BAN-T3 (ronda #20837): quien llama puede confirmar por su propio camino (la conciliacion de banco guarda por la
    // API, porque el webhook conciliacion-banco/apply nunca existio en n8n). Sin `confirmar`, el webhook de siempre.
    if (confirmar) {
      const t0 = Date.now();
      try {
        const r = await confirmar({ aprobaciones, tabla: rows });
        ok = !!r?.ok;
        response = { status: ok ? 200 : (r?.code ?? 0), body: r?.body ?? null, error: ok ? undefined : (r?.error || 'no se pudo guardar'), ms: Date.now() - t0 };
      } catch (err) {
        response = { error: err?.message || String(err) };
        ok = false;
      }
    } else try {
      const t0 = Date.now();
      // Defensive payload: enviar tanto aprobaciones (filtrado MATCH) como tabla raw
      // para que el workflow tenga ambas representaciones disponibles.
      const matchBody = {
        business_id: businessId,
        aprobaciones,
        tabla: rows
      };
      if (facturaId) matchBody.factura_id = facturaId;
      const res = await fetch(matchWebhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(matchBody)
      });
      const ms = Date.now() - t0;
      let body = null;
      // Leer text() primero y luego intentar parse (evita "body stream already read"
      // si el body no es JSON parseable). El workflow Purchase Match retorna shape
      // Gemini { content: { parts: [{ text: "<JSON-string>" }] } } o JSON directo.
      const rawText = await res.text();
      let parsedTop = null;
      try { parsedTop = JSON.parse(rawText); } catch { parsedTop = null; }
      if (parsedTop) {
        const geminiText = parsedTop?.content?.parts?.[0]?.text;
        if (typeof geminiText === 'string') {
          try {
            body = JSON.parse(geminiText.replace(/```json\s*/i, '').replace(/```/g, '').trim());
          } catch { body = parsedTop; }
        } else {
          body = parsedTop;
        }
      } else {
        body = rawText;
      }
      response = { status: res.status, body, ms };
      ok = res.ok;
    } catch (err) {
      response = { error: err?.message || String(err) };
      ok = false;
    }

    if (ok) {
      ctx.emit('mode-action', { mode: 'match-review', action: 'confirmed', data: { aprobaciones, response } });
      if (onConfirmed) {
        try { onConfirmed({ ok: true, response, aprobaciones }); }
        catch (e) { console.error('[match-review] onConfirmed threw:', e); }
      }
      // BAN-T3: con `confirmar` propio (conciliacion de banco) el resultado NO es una compra ni toca inventario: se dice
      // cuantos quedaron guardados, con los textos del caller.
      if (confirmar) {
        const n = response?.body?.aprobados_match ?? aprobaciones.length;
        container.innerHTML = `
          <div class="aa-match-review aa-match-result" data-aa-confirmado style="padding:18px 20px">
            <h3 style="margin:0 0 10px;font-size:1rem;color:#3fb950">✓ ${ctx.escHtml(LBL.confirmado)}</h3>
            <p style="margin:0;font-size:.9rem;color:#c9d1d9"><strong>${n}</strong> ${ctx.escHtml(LBL.itemNoun)}${n === 1 ? '' : 's'} ${n === 1 ? 'quedó' : 'quedaron'} ${ctx.escHtml(LBL.confirmadoComo)}${n === 1 ? '' : 's'}.</p>
            <div style="display:flex;justify-content:flex-end;margin-top:16px">
              <button data-aa-result-close-btn type="button" style="padding:8px 18px;background:#238636;border:1px solid #238636;border-radius:5px;color:#fff;cursor:pointer;font-size:0.875rem;font-weight:500">Listo</button>
            </div>
          </div>`;
        container.querySelector('[data-aa-result-close-btn]')?.addEventListener('click', () => ctx.clear());
        return;
      }
      // Resumen no-tecnico para colaborador/cajero. Lenguaje simple, sin jerga (OCR,
      // asociaciones proveedor-insumo, factura_id). Mostrar valor egreso + cantidad real
      // del/los items aprobados como MATCH (con nombre insumo legible).
      const r = response?.body || {};
      const aprobMatch = r.aprobados_match ?? aprobaciones.filter(a => a.accion === 'MATCH').length;
      const aprobCreate = r.aprobados_create ?? aprobaciones.filter(a => a.accion === 'CREATE').length;
      const aprobReview = r.aprobados_review ?? aprobaciones.filter(a => a.accion === 'REVIEW').length;

      // PHASE-2 T11 (bit #938): por cada item aprobado mostrar CANTIDAD UNIDAD NOMBRE
      // (presentacion) + status legible para que el usuario sepa exactamente que se ingreso.
      // Status:
      //   - 'encolado al Desktop'  : MATCH normal (default).
      //   - 'pendiente conversion' : flag requiere_conversion=true (TODO T9b: backend lo emite).
      //   - 'insumo nuevo - configurar' : si CREATE (sin insumo_id en catalogo).
      // Filtrar directo por row.accion (no por aprobaciones) — aprobaciones solo trae MATCH+insumo_id
      // pero el resumen UX debe mostrar tambien los CREATE para que el usuario sepa que items
      // quedaron pendientes de configuracion (T9c).
      const matchedRows = rows.filter(row => row.accion === 'MATCH' || row.accion === 'CREATE');

      const fmtCantidad = (n, u) => {
        if (n === null || n === undefined || isNaN(Number(n))) return '';
        const num = Number(n);
        const numStr = Number.isInteger(num) ? String(num) : num.toLocaleString('es-CO', { maximumFractionDigits: 3 });
        return u ? `${numStr} ${u}` : numStr;
      };

      // T13 (bit #952): formatea numero en es-CO con separador de miles, sin decimales innecesarios.
      const fmtNumero = (n) => {
        if (n === null || n === undefined || isNaN(Number(n))) return '';
        const num = Number(n);
        return num.toLocaleString('es-CO', { maximumFractionDigits: 3 });
      };

      // T13: parsea presentacion ("2.5kg", "1L", "500g", "1.36 kg") -> { valor, unidad_base }.
      // Convierte a unidad base canonica (gramos para masa, mililitros para volumen, unidades para conteo).
      const parsePresentacion = (p) => {
        if (!p || typeof p !== 'string') return null;
        const m = String(p).trim().match(/^(\d+(?:[.,]\d+)?)\s*(kg|kilos?|gr?|gramos?|mg|l|lt|litros?|ml|cc|und|u|unidades?)\.?\s*$/i);
        if (!m) return null;
        const num = parseFloat(m[1].replace(',', '.'));
        if (!isFinite(num) || num <= 0) return null;
        const unitRaw = m[2].toLowerCase();
        // masa -> gramos
        if (unitRaw === 'kg' || unitRaw.startsWith('kilo')) return { valor: num * 1000, unidad: 'gramos' };
        if (unitRaw === 'g' || unitRaw === 'gr' || unitRaw.startsWith('gramo')) return { valor: num, unidad: 'gramos' };
        if (unitRaw === 'mg') return { valor: num / 1000, unidad: 'gramos' };
        // volumen -> mililitros
        if (unitRaw === 'l' || unitRaw === 'lt' || unitRaw.startsWith('litro')) return { valor: num * 1000, unidad: 'mililitros' };
        if (unitRaw === 'ml' || unitRaw === 'cc') return { valor: num, unidad: 'mililitros' };
        // conteo
        if (unitRaw === 'und' || unitRaw === 'u' || unitRaw.startsWith('unidad')) return { valor: num, unidad: 'unidades' };
        return null;
      };

      const movimientosLegibles = matchedRows.map(row => {
        const ap = aprobaciones.find(a => a.item_id === row.item_id);
        const cand = ap ? row.candidatos.find(c => c.insumo_id === ap.insumo_id) : null;
        const isCreate = row.accion === 'CREATE';
        // Nombre legible: del candidato matcheado, del create_sugerido si CREATE,
        // o fallback a la descripcion raw de la factura.
        const nombre = cand?.nombre
          || row.create_sugerido?.nombre
          || row.factura_descripcion
          || 'insumo';
        const presentacion = (cand?.presentacion || row.create_sugerido?.presentacion || row.presentacion || '').trim();
        const unidad = cand?.unidad || row.create_sugerido?.unidad || row.unidad || '';

        // T13 (bit #952): cantidad total convertida.
        // Si la presentacion es parseable (kg/g/l/ml/und) computamos:
        //   - totalLegible: "12500 gramos"  (cantidad * presentacion en unidad base)
        //   - desgloseLegible: "5 paquetes x 2.5 kg"
        // Fallback: cantidadTxt simple ("48 X 6") si no se puede parsear.
        const presParsed = parsePresentacion(presentacion);
        const cantidadFactura = Number(row.cantidad);
        let totalLegible = '';
        let desgloseLegible = '';
        if (presParsed && isFinite(cantidadFactura) && cantidadFactura > 0) {
          const total = cantidadFactura * presParsed.valor;
          totalLegible = `${fmtNumero(total)} ${presParsed.unidad}`;
          const sustantivo = cantidadFactura === 1 ? 'paquete' : 'paquetes';
          desgloseLegible = `${fmtNumero(cantidadFactura)} ${sustantivo} × ${presentacion}`;
        }
        const cantidadTxt = fmtCantidad(row.cantidad, unidad);

        // Status segun accion + flag requiere_conversion (T9b)
        let status, statusColor;
        if (isCreate) {
          status = 'insumo nuevo - configurar en catalogo';
          statusColor = '#1f6feb';
        } else if (row.requiere_conversion) {
          status = 'pendiente conversion';
          statusColor = '#d29922';
        } else {
          status = 'encolado al Desktop';
          statusColor = '#3fb950';
        }
        return { nombre, presentacion, cantidadTxt, totalLegible, desgloseLegible, status, statusColor, isCreate };
      });

      const movsHtml = movimientosLegibles.length > 0
        ? movimientosLegibles.map(m => {
            // T13 formato: "Nombre — 12500 gramos (5 paquetes × 2.5 kg) — encolado al Desktop"
            // Fallback (sin presentacion parseable): "48 X 6 Nombre (presentacion) — status"
            if (m.totalLegible) {
              return `
                <div style="display:flex;align-items:baseline;gap:6px;flex-wrap:wrap;line-height:1.5">
                  <strong style="color:#f0f6fc">${ctx.escHtml(m.nombre)}</strong>
                  <span style="color:#8b949e">—</span>
                  <strong style="color:#f0f6fc">${ctx.escHtml(m.totalLegible)}</strong>
                  <span style="color:#8b949e;font-size:0.875rem">(${ctx.escHtml(m.desgloseLegible)})</span>
                  <span style="color:${m.statusColor};font-size:0.875rem">— ${ctx.escHtml(m.status)}</span>
                </div>
              `;
            }
            return `
              <div style="display:flex;align-items:baseline;gap:6px;flex-wrap:wrap;line-height:1.5">
                ${m.cantidadTxt ? `<strong style="color:#f0f6fc">${ctx.escHtml(m.cantidadTxt)}</strong>` : ''}
                <strong style="color:#f0f6fc">${ctx.escHtml(m.nombre)}</strong>${m.presentacion ? `<span style="color:#8b949e">(${ctx.escHtml(m.presentacion)})</span>` : ''}
                <span style="color:${m.statusColor};font-size:0.875rem">— ${ctx.escHtml(m.status)}</span>
              </div>
            `;
          }).join('')
        : '<em style="color:#8b949e">Ningun item aprobado para inventario</em>';

      const totalStr = fmtMoney(facturaTotal);
      const facturaCodigoStr = facturaCodigo ? ' (factura ' + ctx.escHtml(facturaCodigo) + ')' : '';

      // REACTIVIDAD-NEGOCIO T3: enriquecer resumen con metodo pago + sumatoria items + link arqueo
      const r2 = response?.body || {};
      const metodoPago = r2.metodo_pago || r2.egreso?.metodo || payload?.metodoPago || 'Caja';
      const egresoStatus = r2.egreso?.status || r2.egreso_status || 'pendiente';
      const egresoId = r2.egreso_id || r2.egreso?.id || null;
      const statusEgresoLabel = egresoStatus === 'pagado' ? 'pagado' : 'pendiente de pago';
      const metodoColor = metodoPago.toLowerCase().includes('caja') ? '#3fb950'
        : metodoPago.toLowerCase().includes('credit') ? '#d29922'
        : metodoPago.toLowerCase().includes('banco') ? '#58a6ff' : '#8b949e';

      // Sumatoria cantidad items aprobados
      const cantidadTotalItems = rows
        .filter(row => row.accion === 'MATCH' || row.accion === 'CREATE')
        .reduce((s, row) => s + (parseFloat(row.cantidad) || 0), 0);
      const cantidadTotalStr = cantidadTotalItems > 0
        ? `<span style="color:#8b949e;font-size:0.875rem">· total <strong style="color:#f0f6fc">${cantidadTotalItems.toLocaleString('es-CO', { maximumFractionDigits: 2 })}</strong> unidades ingresadas</span>`
        : '';
      const itemsCountStr = `<strong>${aprobMatch + aprobCreate}</strong> item${(aprobMatch + aprobCreate) === 1 ? '' : 's'}`;

      // Link a arqueo del dia. Si hay egresoId, intentar focus; sino ir a fecha.
      const fechaHoy = new Date().toISOString().slice(0, 10);
      const arqueoUrl = egresoId ? `#/arqueo?date=${fechaHoy}&focus=${egresoId}` : `#/arqueo?date=${fechaHoy}`;

      container.innerHTML = `
        <div class="aa-match-review aa-match-result" style="padding:18px 20px">
          <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:10px;margin-bottom:14px">
            <h3 style="margin:0;font-size:1rem;color:#3fb950">✓ Compra registrada${facturaCodigoStr}</h3>
            <button data-aa-result-close type="button" title="Cerrar"
                    style="background:transparent;border:none;color:#8b949e;cursor:pointer;font-size:1.1rem">×</button>
          </div>
          <div style="font-size:0.9rem;color:#c9d1d9;line-height:1.8">
            <div style="display:grid;grid-template-columns:160px 1fr;gap:8px;align-items:start">
              <div style="color:#8b949e">💰 Egreso registrado:</div>
              <div>
                <strong style="color:#f0f6fc;font-size:1.05rem">${totalStr}</strong>
                <span style="background:${metodoColor}22;color:${metodoColor};padding:2px 8px;border-radius:10px;font-size:0.875rem;font-weight:600;margin-left:6px">${ctx.escHtml(metodoPago)}</span>
                <span style="color:#8b949e;font-size:0.875rem;margin-left:4px">(${statusEgresoLabel})</span>
              </div>

              <div style="color:#8b949e">📦 Inventario actualizado:</div>
              <div>
                <div style="margin-bottom:4px">${itemsCountStr} ${cantidadTotalStr}</div>
                ${movsHtml}
              </div>

              ${aprobReview > 0 ? `
                <div style="color:#8b949e">🔍 En revision:</div>
                <div><strong>${aprobReview}</strong> item(s) — un administrador revisara estos casos.</div>
              ` : ''}

              ${aprobCreate > 0 ? `
                <div style="color:#8b949e">✨ Insumos nuevos:</div>
                <div><strong>${aprobCreate}</strong> insumo(s) nuevo(s) agregado(s) al catalogo.</div>
              ` : ''}
            </div>
          </div>
          <div style="display:flex;gap:8px;justify-content:space-between;align-items:center;margin-top:18px;flex-wrap:wrap">
            <a href="${arqueoUrl}" data-aa-result-arqueo style="text-decoration:none">
              <button type="button" style="padding:6px 14px;background:#0d1117;border:1px solid #1f6feb;border-radius:5px;color:#58a6ff;cursor:pointer;font-size:0.875rem;font-weight:500">
                Ver en arqueo del dia →
              </button>
            </a>
            <button data-aa-result-close-btn type="button"
                    style="padding:8px 18px;background:#238636;border:1px solid #238636;border-radius:5px;color:#fff;cursor:pointer;font-size:0.875rem;font-weight:500">Listo</button>
          </div>
        </div>
      `;
      const $closeX = container.querySelector('[data-aa-result-close]');
      const $closeBtn = container.querySelector('[data-aa-result-close-btn]');
      if ($closeX) $closeX.addEventListener('click', () => ctx.clear());
      if ($closeBtn) $closeBtn.addEventListener('click', () => ctx.clear());
      // Link arqueo: respetar router hash; cerrar action-area al navegar
      const $arqueoLink = container.querySelector('[data-aa-result-arqueo]');
      if ($arqueoLink) $arqueoLink.addEventListener('click', () => { setTimeout(() => ctx.clear(), 60); });
    } else {
      if ($status) {
        $status.style.color = '#f85149';
        $status.textContent = `✗ Error: ${response?.error || `HTTP ${response?.status}`}. Revisa consola.`;
      }
      console.error('[match-review] webhook error:', response);
      if ($confirmBtn) {
        $confirmBtn.disabled = false;
        $confirmBtn.textContent = `✓ Confirmar matcheo (${aprobaciones.length})`;
        $confirmBtn.style.cursor = 'pointer';
      }
      if (onConfirmed) {
        try { onConfirmed({ ok: false, response, aprobaciones }); }
        catch (e) { console.error('[match-review] onConfirmed threw:', e); }
      }
    }
  }

  async function exit() {
    // T3 PANEL-POST-OCR-MATCH-REDIRECT-V1-001 (bit #971): bloquear cierre con confirm()
    // override si quedan items sin confirmar. Razon: cerrar sin click "Confirmar matcheo"
    // deja items match_status=PENDING en BD y movimiento_inventario nunca se crea
    // (el flujo termina silenciosamente roto, caso CH 196480).
    // Como exit() solo se llama del Cancelar/X del estado pre-confirm, siempre hay
    // rows sin resolver. El user puede override si decide cerrar conscientemente.
    if (rows.length > 0) {
      const okClose = await preguntarEnPagina({
        titulo: 'Quedan items sin confirmar',
        texto: LBL.exitConfirm(rows.length),
        aceptar: 'Cerrar de todos modos',
        cancelar: 'Seguir revisando',
        peligro: true
      });
      if (!okClose) return;
    }
    ctx.emit('mode-action', { mode: 'match-review', action: 'exited', data: {} });
    if (onExit) {
      try { onExit(); } catch (e) { console.error('[match-review] onExit threw:', e); }
    }
    ctx.clear();
  }
}

function expandContainer(container) {
  Object.assign(container.style, {
    maxHeight: '560px',
    opacity: '1',
    margin: '12px 0',
    border: '1px solid #1f6feb',
    padding: '0'
  });
}
